import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { BANNER_PRESETS } from "@/lib/banner";
import { toast } from "sonner";

const KEY_STORE = "kc_admin_key";
const BLANK = { enabled: false, preset: "custom", title: "", subtitle: "", cta_text: "Shop Now", cta_link: "/shop", bg: "#B84A3B", fg: "#FFFFFF" };

export default function AdminBanner() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORE) || "");
  const [cfg, setCfg] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/banner").then((r) => setCfg({ ...BLANK, ...r.data })).catch(() => {});
  }, []);

  const applyPreset = (p) => setCfg((c) => ({
    ...c, preset: p.id, title: p.title, subtitle: p.subtitle,
    cta_text: p.cta_text, cta_link: p.cta_link, bg: p.bg, fg: p.fg, enabled: true,
  }));

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    if (!adminKey) { toast.error("Enter your admin key first."); return; }
    localStorage.setItem(KEY_STORE, adminKey);
    setSaving(true);
    try {
      await api.put("/settings/banner", cfg, { headers: { "X-Admin-Key": adminKey } });
      toast.success("Banner saved. It's live on the homepage.");
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Invalid admin key." : "Could not save. Try again.");
    } finally { setSaving(false); }
  };

  const inputCls = "w-full border border-[#E5E5DF] px-3 py-2.5 text-sm font-body focus:outline-none focus:border-[#1A1A1A]";

  return (
    <main className="max-w-[820px] mx-auto px-5 sm:px-8 py-14 min-h-[70vh]" data-testid="admin-banner-page">
      <Seo title="Banner Admin" path="/admin/banner" noindex />
      <div className="overline text-[#B84A3B] mb-2">Store Admin</div>
      <h1 className="font-display text-4xl tracking-tight mb-2">Seasonal Homepage Banner</h1>
      <p className="text-sm text-[#6E6E69] mb-8 font-body">Pick a festival, tweak the text, and hit save — it goes live on the homepage instantly.</p>

      {/* Admin key */}
      <label className="block mb-6">
        <span className="overline text-[#1A1A1A] block mb-1.5">Admin Key</span>
        <input type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)} placeholder="Enter admin key"
          data-testid="admin-key-input" className={inputCls} />
      </label>

      {/* Presets */}
      <span className="overline text-[#1A1A1A] block mb-2">Quick Presets (2026 Festivals)</span>
      <div className="flex flex-wrap gap-2 mb-8">
        {BANNER_PRESETS.map((p) => (
          <button key={p.id} onClick={() => applyPreset(p)} data-testid={`preset-${p.id}`}
            className={`px-4 py-2 text-[0.7rem] font-mono tracking-[0.1em] uppercase border transition-colors ${cfg.preset === p.id ? "bg-[#1A1A1A] text-white border-[#1A1A1A]" : "border-[#E5E5DF] hover:border-[#1A1A1A]"}`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Live preview */}
      <span className="overline text-[#1A1A1A] block mb-2">Live Preview</span>
      <div className="mb-8 flex items-center justify-center gap-4 flex-wrap px-5 py-3 text-center" style={{ backgroundColor: cfg.bg, color: cfg.fg }} data-testid="banner-preview">
        <span className="font-display text-lg">{cfg.title || "Your banner title"}</span>
        {cfg.subtitle && <span className="hidden sm:inline text-sm opacity-85">{cfg.subtitle}</span>}
        {cfg.cta_text && <span className="text-[0.66rem] font-mono tracking-[0.16em] uppercase border-b pb-0.5" style={{ borderColor: cfg.fg }}>{cfg.cta_text} →</span>}
      </div>

      {/* Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <label className="sm:col-span-2"><span className="overline text-[#1A1A1A] block mb-1.5">Title</span>
          <input value={cfg.title} onChange={(e) => setField("title", e.target.value)} data-testid="banner-title-input" className={inputCls} /></label>
        <label className="sm:col-span-2"><span className="overline text-[#1A1A1A] block mb-1.5">Subtitle</span>
          <input value={cfg.subtitle || ""} onChange={(e) => setField("subtitle", e.target.value)} data-testid="banner-subtitle-input" className={inputCls} /></label>
        <label><span className="overline text-[#1A1A1A] block mb-1.5">Button Text</span>
          <input value={cfg.cta_text || ""} onChange={(e) => setField("cta_text", e.target.value)} data-testid="banner-cta-text-input" className={inputCls} /></label>
        <label><span className="overline text-[#1A1A1A] block mb-1.5">Button Link</span>
          <input value={cfg.cta_link || ""} onChange={(e) => setField("cta_link", e.target.value)} data-testid="banner-cta-link-input" className={inputCls} placeholder="/shop?collection=..." /></label>
        <label><span className="overline text-[#1A1A1A] block mb-1.5">Background Colour</span>
          <div className="flex items-center gap-2"><input type="color" value={cfg.bg} onChange={(e) => setField("bg", e.target.value)} className="h-11 w-14 border border-[#E5E5DF]" /><input value={cfg.bg} onChange={(e) => setField("bg", e.target.value)} className={inputCls} /></div></label>
        <label><span className="overline text-[#1A1A1A] block mb-1.5">Text Colour</span>
          <div className="flex items-center gap-2"><input type="color" value={cfg.fg} onChange={(e) => setField("fg", e.target.value)} className="h-11 w-14 border border-[#E5E5DF]" /><input value={cfg.fg} onChange={(e) => setField("fg", e.target.value)} className={inputCls} /></div></label>
      </div>

      {/* Enable toggle */}
      <label className="flex items-center gap-3 mb-8 cursor-pointer" data-testid="banner-enabled-toggle">
        <input type="checkbox" checked={cfg.enabled} onChange={(e) => setField("enabled", e.target.checked)} className="w-5 h-5 accent-[#B84A3B]" />
        <span className="font-body text-sm">Show this banner on the homepage</span>
      </label>

      <button onClick={save} disabled={saving} data-testid="banner-save-button"
        className="w-full sm:w-auto bg-[#1A1A1A] text-[#FFFFFF] px-10 py-4 text-[0.72rem] font-mono tracking-[0.22em] uppercase hover:bg-[#B84A3B] transition-colors disabled:opacity-60">
        {saving ? "Saving…" : "Save & Publish"}
      </button>
    </main>
  );
}
